import { useCallback, useRef, useState } from 'react';
import { cn } from '@rocco/utils/cn';

import type { B2BAssets, MediaBandSection } from '../types';

export interface B2BMediaBandProps {
    section: MediaBandSection;
    assets: B2BAssets;
}

/**
 * Media band (Figma `480:29811`, 1920 × 639): an inset video / image thumbnail
 * with side margins of ~190px showing the page background. The inner image
 * frame (`480:29812`) measures 1539.021 × 646.068 and sits at left=190.71 /
 * top=-6.88 — i.e. it overflows the section top/bottom by ~7px each.
 *
 * For videos the section renders the Figma play-button overlay (96 × 95, node
 * `480:29813`) on top of the first frame and starts playback on click. The
 * video does NOT autoplay — this band is a deliberate viewer interaction
 * (a client-testimonials reel that may have voice / audio), distinct from
 * the silent looping media-program hero above.
 */
export const B2BMediaBand = ({ section, assets }: B2BMediaBandProps) => {
    const src = assets[section.assetKey];
    const webmSrc = section.webmAssetKey
        ? assets[section.webmAssetKey]
        : undefined;

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [isPlaying, setIsPlaying] = useState(false);

    const handlePlay = useCallback(() => {
        const v = videoRef.current;
        if (!v) return;
        // Unmute when the user clicks play — browsers block sound-on autoplay
        // but a user gesture lifts that restriction, so the testimonial reel
        // plays with audio as designed.
        v.muted = false;
        void v.play();
    }, []);

    return (
        <section
            data-b2b-media-band
            // Mobile (Figma `521:27470`, 768×286): aspect 2.685.
            className="relative w-full overflow-hidden bg-white max-md:aspect-[768/286] md:aspect-[1920/639]"
        >
            {/* Inner media frame.
             *  - Desktop (1920×639): left ≈ 9.93 %, top ≈ -1.08 %, w ≈ 80.16 %.
             *  - Mobile (768×286): left ≈ 5.71 %, top 0, w ≈ 88.57 %, full-height. */}
            <div
                className="absolute left-[9.933%] top-[-1.077%] aspect-[1539.021/646.068] w-[80.157%] overflow-hidden max-md:!left-[5.713%] max-md:!top-0 max-md:!aspect-[680.24/285.56] max-md:!w-[88.573%]"
                data-node-id="480:29812"
            >
                {section.isVideo ? (
                    <>
                        <video
                            ref={videoRef}
                            className="absolute inset-0 size-full object-cover"
                            // Pre-load the first frame so the play-button
                            // overlay sits on top of the video poster, not a
                            // black rectangle. No autoplay — the user clicks
                            // to start (see Figma 480:29813).
                            preload="metadata"
                            muted
                            playsInline
                            onPlay={() => setIsPlaying(true)}
                            onPause={() => setIsPlaying(false)}
                            onEnded={() => setIsPlaying(false)}
                            // Clicking the video itself also starts it once
                            // the overlay's been dismissed — easy re-play.
                            onClick={() => {
                                const v = videoRef.current;
                                if (!v) return;
                                if (v.paused) void v.play();
                                else v.pause();
                            }}
                        >
                            {/* WebM first — Chrome / Firefox pick it
                             *  (smaller). Safari + iOS fall through to mp4. */}
                            {webmSrc && (
                                <source src={webmSrc} type="video/webm" />
                            )}
                            <source src={src} type="video/mp4" />
                        </video>

                        {/* Play-button overlay (Figma 480:29813 — 96×95 circle
                         *  with backdrop-blur, white @ 40 % opacity, #D9D9D9
                         *  triangle). Centred on the 1539×646 inner frame
                         *  (96/1539 = 6.237 % wide). Hidden while playing. */}
                        <button
                            type="button"
                            onClick={handlePlay}
                            aria-label="Play video"
                            className={cn(
                                'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                                'aspect-[96/95] w-[6.237%] rounded-full',
                                'bg-white/40 backdrop-blur-[17px]',
                                'flex items-center justify-center',
                                'transition-opacity duration-200 hover:bg-white/55',
                                isPlaying
                                    ? 'pointer-events-none opacity-0'
                                    : 'opacity-100',
                            )}
                        >
                            {/* Triangle — Figma `Polygon 10`, #D9D9D9 fill. */}
                            <svg
                                viewBox="0 0 96 95"
                                aria-hidden
                                className="size-full"
                            >
                                <path
                                    d="M67.8984 47.2212L37.8743 64.5557L37.8743 29.8867L67.8984 47.2212Z"
                                    fill="#D9D9D9"
                                />
                            </svg>
                        </button>
                    </>
                ) : (
                    <img
                        className="absolute inset-0 size-full object-cover"
                        src={src}
                        alt={section.label ?? ''}
                    />
                )}
            </div>
        </section>
    );
};
